// The catalog's last load, kept across workspace tab switches (#282 C2): the
// catalog unmounts whenever another view opens, and used to download every
// collection, product, redirect and count again on each return.
//
// It is reused while it is fresh and nothing has been written since:
//   - any successful write to the BasiQR API (POST/PUT/PATCH/DELETE) drops it,
//     whichever view or service made it — a create form, a bulk import, an
//     edit, a team change. Over-dropping only costs one reload;
//   - it expires after CATALOG_FRESH_MS, so changes made elsewhere (a team
//     member, an API key, MCP) show up on a later visit.

export const CATALOG_FRESH_MS = 2 * 60_000;

export interface CatalogSnapshot<C = unknown, P = unknown> {
  collections: C[];
  products: P[];
  countsLoaded: boolean;
}

let snapshot: (CatalogSnapshot & { savedAt: number }) | null = null;

/** The last load, or null when there is none, it expired, or a write dropped it. */
export function readCatalogSnapshot<C, P>(now = Date.now()): CatalogSnapshot<C, P> | null {
  if (!snapshot || now - snapshot.savedAt >= CATALOG_FRESH_MS) return null;
  return snapshot as CatalogSnapshot<C, P>;
}

export function saveCatalogSnapshot(next: CatalogSnapshot, now = Date.now()): void {
  snapshot = { ...next, savedAt: now };
}

export function invalidateCatalogSnapshot(): void {
  snapshot = null;
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const requestUrl = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

const requestMethod = (input: RequestInfo | URL, init?: RequestInit): string =>
  (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();

/** Wrap window.fetch once so every successful API write drops the snapshot. */
export function installCatalogInvalidation(apiBase: string): void {
  const marker = '__basiqrCatalogInvalidation';
  if (!apiBase || (window.fetch as any)[marker]) return;
  const originalFetch = window.fetch.bind(window);

  const wrapped = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    if (response.ok && WRITE_METHODS.has(requestMethod(input, init)) && requestUrl(input).startsWith(apiBase)) {
      invalidateCatalogSnapshot();
    }
    return response;
  };
  (wrapped as any)[marker] = true;
  window.fetch = wrapped as typeof window.fetch;
}
